import React from 'react';
import { 
  Box, 
  Typography, 
  Paper,
  Container,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import { 
  FiberManualRecord as BulletIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

function PrivacyPolicy() {
  const navigate = useNavigate();
  const isLoggedIn = localStorage.getItem('token');

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {/* Only show Back button if user is not logged in */}
        {!isLoggedIn ? (
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center',
              cursor: 'pointer',
              color: '#6b7280',
              '&:hover': { color: '#374151' }
            }}
            onClick={handleBack}
          >
            <ArrowBackIcon sx={{ mr: 1, fontSize: 20 }} />
            <Typography variant="body2">Back</Typography>
          </Box>
        ) : (
          <Box sx={{ width: '80px' }} />
        )}
        
        <Typography 
          variant="h4" 
          sx={{ 
            fontWeight: 700, 
            color: '#111827',
            flex: 1,
            textAlign: 'center'
          }}
        >
          Privacy Policy
        </Typography>
        
        {/* Empty div to balance the layout */}
        <Box sx={{ width: '80px' }} />
      </Box>

      {/* Content */}
      <Paper sx={{ p: { xs: 3, md: 4 }, boxShadow: 'none', border: '1px solid #e5e7eb' }}>
        
                 {/* Header */}
         <Box sx={{ mb: 4, textAlign: 'left', pb: 3, borderBottom: '2px solid #e5e7eb' }}>
           {/* <Typography variant="h4" sx={{ fontWeight: 700, color: '#111827', mb: 2 }}>
             Privacy Policy
           </Typography> */}
           <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 600 }}>
             Effective Date: August 29, 2025
           </Typography>
         </Box>

         {/* 1. Introduction */}
         <Box sx={{ mb: 4 }}>
           <Typography variant="h5" sx={{ fontWeight: 600, color: '#111827', mb: 2 }}>
             1. Introduction
           </Typography>
           <Typography variant="body1" sx={{ color: '#374151', lineHeight: 1.6, mb: 2 }}>
              <Box component="span" sx={{ fontWeight: 'bold' }}>AgentZena</Box> ("we," "our," or "us") is a product of <Box component="span" sx={{ fontWeight: 'bold' }}>KonnectBio Inc.</Box> This Privacy Policy explains how we collect, use, disclose, and protect personal information when you install and use the <Box component="span" sx={{ fontWeight: 'bold' }}>AgentZena Shopify App</Box> ("<Box component="span" sx={{ fontWeight: 'bold' }}>App</Box>") and when you access the <Box component="span" sx={{ fontWeight: 'bold' }}>manage.agentzena.ai portal</Box> ("<Box component="span" sx={{ fontWeight: 'bold' }}>Portal</Box>").
            </Typography>
            <Typography variant="body1" sx={{ color: '#374151', lineHeight: 1.6 }}>
              By installing the <Box component="span" sx={{ fontWeight: 'bold' }}>App</Box>, connecting it to your <Box component="span" sx={{ fontWeight: 'bold' }}>Shopify store</Box>, or using the <Box component="span" sx={{ fontWeight: 'bold' }}>Portal</Box>, you agree to this Privacy Policy.
            </Typography>
         </Box>

                 {/* 2. Information We Collect */}
         <Box sx={{ mb: 4 }}>
           <Typography variant="h5" sx={{ fontWeight: 600, color: '#111827', mb: 2 }}>
             2. Information We Collect
           </Typography>
           
           <Typography variant="h6" sx={{ fontWeight: 600, color: '#374151', mb: 1, mt: 2 }}>
             2.1 From Merchants (Brands)
           </Typography>
           <Typography variant="body1" sx={{ color: '#374151', lineHeight: 1.6, mb: 2 }}>
             When you install <Box component="span" sx={{ fontWeight: 'bold' }}>AgentZena</Box> from the <Box component="span" sx={{ fontWeight: 'bold' }}>Shopify App Store</Box>, we collect:
           </Typography>
           <List dense sx={{ pl: 2 }}>
             <ListItem sx={{ py: 0.5 }}>
               <ListItemIcon sx={{ minWidth: 20 }}>
                 <BulletIcon sx={{ fontSize: 8, color: '#6b7280' }} />
               </ListItemIcon>
               <ListItemText 
                 primary={
                   <Box>
                     <Box component="span" sx={{ fontWeight: 'bold' }}>Shopify account details</Box>: store name, store domain, contact email, owner name, and Shopify account ID.
                   </Box>
                 }
                 sx={{ '& .MuiListItemText-primary': { color: '#374151', fontSize: '14px' } }}
               />
             </ListItem>
             <ListItem sx={{ py: 0.5 }}>
               <ListItemIcon sx={{ minWidth: 20 }}>
                 <BulletIcon sx={{ fontSize: 8, color: '#6b7280' }} />
               </ListItemIcon>
               <ListItemText 
                 primary={
                   <Box>
                     <Box component="span" sx={{ fontWeight: 'bold' }}>Authentication tokens</Box>: provided by Shopify to enable integration.
                   </Box>
                 }
                 sx={{ '& .MuiListItemText-primary': { color: '#374151', fontSize: '14px' } }}
               />
             </ListItem>
             <ListItem sx={{ py: 0.5 }}>
               <ListItemIcon sx={{ minWidth: 20 }}>
                 <BulletIcon sx={{ fontSize: 8, color: '#6b7280' }} />
               </ListItemIcon>
               <ListItemText 
                 primary={
                   <Box>
                     <Box component="span" sx={{ fontWeight: 'bold' }}>Subscription and billing information</Box>: plan details, payment status, and billing contact information
                   </Box>
                 }
                 sx={{ '& .MuiListItemText-primary': { color: '#374151', fontSize: '14px' } }}
               />
             </ListItem>
             <ListItem sx={{ py: 0.5 }}>
               <ListItemIcon sx={{ minWidth: 20 }}>
                 <BulletIcon sx={{ fontSize: 8, color: '#6b7280' }} />
               </ListItemIcon>
               <ListItemText 
                 primary={
                   <Box>
                     <Box component="span" sx={{ fontWeight: 'bold' }}>Bot configuration settings</Box>: preferences you set in the <Box component="span" sx={{ fontWeight: 'bold' }}>Portal</Box> for <Box component="span" sx={{ fontWeight: 'bold' }}>AgentZena behavior</Box>.
                   </Box>
                 }
                 sx={{ '& .MuiListItemText-primary': { color: '#374151', fontSize: '14px' } }}
               />
             </ListItem>
           </List>

           <Typography variant="h6" sx={{ fontWeight: 600, color: '#374151', mb: 1, mt: 3 }}>
             2.2 From Customers of the Merchant
           </Typography>
           <Typography variant="body1" sx={{ color: '#374151', lineHeight: 1.6, mb: 2 }}>
             When a customer interacts with <Box component="span" sx={{ fontWeight: 'bold' }}>AgentZena</Box> on merchants' store, we may process:
           </Typography>
           <List dense sx={{ pl: 2 }}>
             <ListItem sx={{ py: 0.5 }}>
               <ListItemIcon sx={{ minWidth: 20 }}>
                 <BulletIcon sx={{ fontSize: 8, color: '#6b7280' }} />
               </ListItemIcon>
               <ListItemText 
                 primary={
                   <Box>
                     <Box component="span" sx={{ fontWeight: 'bold' }}>Order-related data</Box>: order number, purchase history, delivery status, and returns information (via <Box component="span" sx={{ fontWeight: 'bold' }}>Shopify APIs</Box>)
                   </Box>
                 }
                 sx={{ '& .MuiListItemText-primary': { color: '#374151', fontSize: '14px' } }}
               />
             </ListItem>
             <ListItem sx={{ py: 0.5 }}>
               <ListItemIcon sx={{ minWidth: 20 }}>
                 <BulletIcon sx={{ fontSize: 8, color: '#6b7280' }} />
               </ListItemIcon>
               <ListItemText 
                 primary={
                   <Box>
                     <Box component="span" sx={{ fontWeight: 'bold' }}>Customer contact details</Box>: name, email address, and phone number (if provided by the customer for order lookup)
                   </Box>
                 }
                 sx={{ '& .MuiListItemText-primary': { color: '#374151', fontSize: '14px' } }}
               />
             </ListItem>
             <ListItem sx={{ py: 0.5 }}>
               <ListItemIcon sx={{ minWidth: 20 }}>
                 <BulletIcon sx={{ fontSize: 8, color: '#6b7280' }} />
               </ListItemIcon>
               <ListItemText 
                 primary={
                   <Box>
                     <Box component="span" sx={{ fontWeight: 'bold' }}>Chat transcripts</Box>: conversation content between the customer and the <Box component="span" sx={{ fontWeight: 'bold' }}>AgentZena bot</Box>
                   </Box>
                 }
                 sx={{ '& .MuiListItemText-primary': { color: '#374151', fontSize: '14px' } }}
               />
             </ListItem>
           </List>

           <Typography variant="h6" sx={{ fontWeight: 600, color: '#374151', mb: 1, mt: 3 }}>
             2.3 Automatically Collected Data
           </Typography>
           <Typography variant="body1" sx={{ color: '#374151', lineHeight: 1.6, mb: 2 }}>
             When accessing the <Box component="span" sx={{ fontWeight: 'bold' }}>Portal</Box> or <Box component="span" sx={{ fontWeight: 'bold' }}>App</Box>, we may collect limited technical information necessary to operate and secure the service, such as:
           </Typography>
           <List dense sx={{ pl: 2 }}>
             <ListItem sx={{ py: 0.5 }}>
               <ListItemIcon sx={{ minWidth: 20 }}>
                 <BulletIcon sx={{ fontSize: 8, color: '#6b7280' }} />
               </ListItemIcon>
               <ListItemText 
                 primary={
                   <Box>
                     <Box component="span" sx={{ fontWeight: 'bold' }}>IP address</Box>
                   </Box>
                 }
                 sx={{ '& .MuiListItemText-primary': { color: '#374151', fontSize: '14px' } }}
               />
             </ListItem>
             <ListItem sx={{ py: 0.5 }}>
               <ListItemIcon sx={{ minWidth: 20 }}>
                 <BulletIcon sx={{ fontSize: 8, color: '#6b7280' }} />
               </ListItemIcon>
               <ListItemText 
                 primary={
                   <Box>
                     <Box component="span" sx={{ fontWeight: 'bold' }}>Browser type</Box>
                   </Box>
                 }
                 sx={{ '& .MuiListItemText-primary': { color: '#374151', fontSize: '14px' } }}
               />
             </ListItem>
             <ListItem sx={{ py: 0.5 }}>
               <ListItemIcon sx={{ minWidth: 20 }}>
                 <BulletIcon sx={{ fontSize: 8, color: '#6b7280' }} />
               </ListItemIcon>
               <ListItemText 
                 primary={
                   <Box>
                     <Box component="span" sx={{ fontWeight: 'bold' }}>Device type</Box>
                   </Box>
                 }
                 sx={{ '& .MuiListItemText-primary': { color: '#374151', fontSize: '14px' } }}
               />
             </ListItem>
             <ListItem sx={{ py: 0.5 }}>
               <ListItemIcon sx={{ minWidth: 20 }}>
                 <BulletIcon sx={{ fontSize: 8, color: '#6b7280' }} />
               </ListItemIcon>
               <ListItemText 
                 primary={
                   <Box>
                     <Box component="span" sx={{ fontWeight: 'bold' }}>Operating system</Box>
                   </Box>
                 }
                 sx={{ '& .MuiListItemText-primary': { color: '#374151', fontSize: '14px' } }}
               />
             </ListItem>
             <ListItem sx={{ py: 0.5 }}>
               <ListItemIcon sx={{ minWidth: 20 }}>
                 <BulletIcon sx={{ fontSize: 8, color: '#6b7280' }} />
               </ListItemIcon>
               <ListItemText 
                 primary={
                   <Box>
                     <Box component="span" sx={{ fontWeight: 'bold' }}>Access timestamps and feature usage</Box> within the <Box component="span" sx={{ fontWeight: 'bold' }}>Portal</Box>
                   </Box>
                 }
                 sx={{ '& .MuiListItemText-primary': { color: '#374151', fontSize: '14px' } }}
               />
             </ListItem>
           </List>
         </Box>

                 {/* 3. Cookies and Similar Technologies */}
         <Box sx={{ mb: 4 }}>
           <Typography variant="h5" sx={{ fontWeight: 600, color: '#111827', mb: 2 }}>
             3. Cookies and Similar Technologies
           </Typography>
           <Typography variant="body1" sx={{ color: '#374151', lineHeight: 1.6, mb: 2 }}>
             <Box component="span" sx={{ fontWeight: 'bold' }}>AgentZena</Box> does not use cookies or tracking technologies for advertising, marketing, behavioral profiling, or analytics purposes.
           </Typography>
           <Typography variant="body1" sx={{ color: '#374151', lineHeight: 1.6, mb: 2 }}>
             Our services may rely on strictly necessary technical mechanisms, such as session-based authentication or security-related technologies, that are required to:
           </Typography>
           <List dense sx={{ pl: 2 }}>
             <ListItem sx={{ py: 0.5 }}>
               <ListItemIcon sx={{ minWidth: 20 }}>
                 <BulletIcon sx={{ fontSize: 8, color: '#6b7280' }} />
               </ListItemIcon>
               <ListItemText 
                 primary="Authenticate users and maintain secure access to the manage.agentzena.ai Portal"
                 sx={{ '& .MuiListItemText-primary': { color: '#374151', fontSize: '14px' } }}
               />
             </ListItem>
             <ListItem sx={{ py: 0.5 }}>
               <ListItemIcon sx={{ minWidth: 20 }}>
                 <BulletIcon sx={{ fontSize: 8, color: '#6b7280' }} />
               </ListItemIcon>
               <ListItemText 
                 primary="Protect the platform against unauthorized access and abuse"
                 sx={{ '& .MuiListItemText-primary': { color: '#374151', fontSize: '14px' } }}
               />
             </ListItem>
             <ListItem sx={{ py: 0.5 }}>
               <ListItemIcon sx={{ minWidth: 20 }}>
                 <BulletIcon sx={{ fontSize: 8, color: '#6b7280' }} />
               </ListItemIcon>
               <ListItemText 
                 primary="Ensure proper integration with Shopify services"
                 sx={{ '& .MuiListItemText-primary': { color: '#374151', fontSize: '14px' } }}
               />
             </ListItem>
           </List>
           <Typography variant="body1" sx={{ color: '#374151', lineHeight: 1.6, mt: 2, mb: 2 }}>
             These mechanisms are essential for the operation and security of the service and do not track users across websites or services.
           </Typography>
           <Typography variant="body1" sx={{ color: '#374151', lineHeight: 1.6 }}>
             Because these technologies are required for core functionality, they cannot be disabled through our platform. Users may control or remove browser-level storage through their browser settings; however, doing so may impact service availability or functionality.
           </Typography>
         </Box>

                 {/* 4. How We Use the Information */}
         <Box sx={{ mb: 4 }}>
           <Typography variant="h5" sx={{ fontWeight: 600, color: '#111827', mb: 2 }}>
             4. How We Use the Information
           </Typography>
           <Typography variant="body1" sx={{ color: '#374151', lineHeight: 1.6, mb: 2 }}>
             We use collected data to:
           </Typography>
           <List dense sx={{ pl: 2 }}>
             <ListItem sx={{ py: 0.5 }}>
               <ListItemIcon sx={{ minWidth: 20 }}>
                 <BulletIcon sx={{ fontSize: 8, color: '#6b7280' }} />
               </ListItemIcon>
               <ListItemText 
                 primary={
                   <Box>
                     Provide and operate the <Box component="span" sx={{ fontWeight: 'bold' }}>AgentZena AI agent</Box> for <Box component="span" sx={{ fontWeight: 'bold' }}>Shopify stores</Box>
                   </Box>
                 }
                 sx={{ '& .MuiListItemText-primary': { color: '#374151', fontSize: '14px' } }}
               />
             </ListItem>
             <ListItem sx={{ py: 0.5 }}>
               <ListItemIcon sx={{ minWidth: 20 }}>
                 <BulletIcon sx={{ fontSize: 8, color: '#6b7280' }} />
               </ListItemIcon>
               <ListItemText 
                 primary="Resolve customer order inquiries and provide shopping assistance"
                 sx={{ '& .MuiListItemText-primary': { color: '#374151', fontSize: '14px' } }}
               />
             </ListItem>
             <ListItem sx={{ py: 0.5 }}>
               <ListItemIcon sx={{ minWidth: 20 }}>
                 <BulletIcon sx={{ fontSize: 8, color: '#6b7280' }} />
               </ListItemIcon>
               <ListItemText 
                 primary={
                   <Box>
                     Configure, maintain, and improve <Box component="span" sx={{ fontWeight: 'bold' }}>bot performance</Box>
                   </Box>
                 }
                 sx={{ '& .MuiListItemText-primary': { color: '#374151', fontSize: '14px' } }}
               />
             </ListItem>
             <ListItem sx={{ py: 0.5 }}>
               <ListItemIcon sx={{ minWidth: 20 }}>
                 <BulletIcon sx={{ fontSize: 8, color: '#6b7280' }} />
               </ListItemIcon>
               <ListItemText 
                 primary={
                   <Box>
                     Authenticate and manage merchant accounts in the <Box component="span" sx={{ fontWeight: 'bold' }}>Portal</Box>
                   </Box>
                 }
                 sx={{ '& .MuiListItemText-primary': { color: '#374151', fontSize: '14px' } }}
               />
             </ListItem>
             <ListItem sx={{ py: 0.5 }}>
               <ListItemIcon sx={{ minWidth: 20 }}>
                 <BulletIcon sx={{ fontSize: 8, color: '#6b7280' }} />
               </ListItemIcon>
               <ListItemText 
                 primary={
                   <Box>
                     Process <Box component="span" sx={{ fontWeight: 'bold' }}>payments</Box> and manage <Box component="span" sx={{ fontWeight: 'bold' }}>subscriptions</Box>
                   </Box>
                 }
                 sx={{ '& .MuiListItemText-primary': { color: '#374151', fontSize: '14px' } }}
               />
             </ListItem>
             <ListItem sx={{ py: 0.5 }}>
               <ListItemIcon sx={{ minWidth: 20 }}>
                 <BulletIcon sx={{ fontSize: 8, color: '#6b7280' }} />
               </ListItemIcon>
               <ListItemText 
                 primary="Monitor service performance and security"
                 sx={{ '& .MuiListItemText-primary': { color: '#374151', fontSize: '14px' } }}
               />
             </ListItem>
             <ListItem sx={{ py: 0.5 }}>
               <ListItemIcon sx={{ minWidth: 20 }}>
                 <BulletIcon sx={{ fontSize: 8, color: '#6b7280' }} />
               </ListItemIcon>
               <ListItemText 
                 primary={
                   <Box>
                     Communicate <Box component="span" sx={{ fontWeight: 'bold' }}>service updates</Box>, <Box component="span" sx={{ fontWeight: 'bold' }}>security alerts</Box>, and <Box component="span" sx={{ fontWeight: 'bold' }}>support messages</Box>
                   </Box>
                 }
                 sx={{ '& .MuiListItemText-primary': { color: '#374151', fontSize: '14px' } }}
               />
             </ListItem>
           </List>
         </Box>

                 {/* 5. How We Share the Information */}
         <Box sx={{ mb: 4 }}>
           <Typography variant="h5" sx={{ fontWeight: 600, color: '#111827', mb: 2 }}>
             5. How We Share the Information
           </Typography>
           <Typography variant="body1" sx={{ color: '#374151', lineHeight: 1.6, mb: 2 }}>
             We do not sell <Box component="span" sx={{ fontWeight: 'bold' }}>personal information</Box>.
           </Typography>
           <Typography variant="body1" sx={{ color: '#374151', lineHeight: 1.6, mb: 2 }}>
             We may share information with:
           </Typography>
           <List dense sx={{ pl: 2 }}>
             <ListItem sx={{ py: 0.5 }}>
               <ListItemIcon sx={{ minWidth: 20 }}>
                 <BulletIcon sx={{ fontSize: 8, color: '#6b7280' }} />
               </ListItemIcon>
               <ListItemText 
                 primary={
                   <Box>
                     <Box component="span" sx={{ fontWeight: 'bold' }}>Service providers</Box>: hosting, infrastructure, and payment processors operating under confidentiality obligations
                   </Box>
                 }
                 sx={{ '& .MuiListItemText-primary': { color: '#374151', fontSize: '14px' } }}
               />
             </ListItem>
             <ListItem sx={{ py: 0.5 }}>
               <ListItemIcon sx={{ minWidth: 20 }}>
                 <BulletIcon sx={{ fontSize: 8, color: '#6b7280' }} />
               </ListItemIcon>
               <ListItemText 
                 primary={
                   <Box>
                     <Box component="span" sx={{ fontWeight: 'bold' }}>Shopify</Box>: as required by <Box component="span" sx={{ fontWeight: 'bold' }}>Shopify API policies</Box> and <Box component="span" sx={{ fontWeight: 'bold' }}>App Store terms</Box>
                   </Box>
                 }
                 sx={{ '& .MuiListItemText-primary': { color: '#374151', fontSize: '14px' } }}
               />
             </ListItem>
             <ListItem sx={{ py: 0.5 }}>
               <ListItemIcon sx={{ minWidth: 20 }}>
                 <BulletIcon sx={{ fontSize: 8, color: '#6b7280' }} />
               </ListItemIcon>
               <ListItemText 
                 primary={
                   <Box>
                     <Box component="span" sx={{ fontWeight: 'bold' }}>Legal authorities</Box>: if required by law or legal process
                   </Box>
                 }
                 sx={{ '& .MuiListItemText-primary': { color: '#374151', fontSize: '14px' } }}
               />
             </ListItem>
             <ListItem sx={{ py: 0.5 }}>
               <ListItemIcon sx={{ minWidth: 20 }}>
                 <BulletIcon sx={{ fontSize: 8, color: '#6b7280' }} />
               </ListItemIcon>
               <ListItemText 
                 primary={
                   <Box>
                     <Box component="span" sx={{ fontWeight: 'bold' }}>Business transfers</Box>: in connection with a merger, acquisition, or sale of assets
                   </Box>
                 }
                 sx={{ '& .MuiListItemText-primary': { color: '#374151', fontSize: '14px' } }}
               />
             </ListItem>
           </List>
         </Box>

                 {/* 6. Data Retention */}
         <Box sx={{ mb: 4 }}>
           <Typography variant="h5" sx={{ fontWeight: 600, color: '#111827', mb: 2 }}>
             6. Data Retention
           </Typography>
           <Typography variant="body1" sx={{ color: '#374151', lineHeight: 1.6 }}>
             We retain <Box component="span" sx={{ fontWeight: 'bold' }}>personal data</Box> only for as long as necessary to provide services, resolve disputes, comply with legal obligations, or enforce our agreements. Data is securely deleted or anonymized when no longer required.
           </Typography>
         </Box>

                 {/* 7. Data Security */}
         <Box sx={{ mb: 4 }}>
           <Typography variant="h5" sx={{ fontWeight: 600, color: '#111827', mb: 2 }}>
             7. Data Security
           </Typography>
           <Typography variant="body1" sx={{ color: '#374151', lineHeight: 1.6 }}>
             We implement reasonable physical, technical, and administrative safeguards designed to protect <Box component="span" sx={{ fontWeight: 'bold' }}>personal data</Box>. While we take security seriously, no method of transmission over the Internet is completely secure, and we cannot guarantee absolute security.
           </Typography>
         </Box>

                 {/* 8. Your Rights */}
         <Box sx={{ mb: 4 }}>
           <Typography variant="h5" sx={{ fontWeight: 600, color: '#111827', mb: 2 }}>
             8. Your Rights
           </Typography>
           <Typography variant="body1" sx={{ color: '#374151', lineHeight: 1.6, mb: 2 }}>
             Depending on your location, you may have certain rights in relation to your <Box component="span" sx={{ fontWeight: 'bold' }}>personal information</Box>, including the right to:
           </Typography>
           <List dense sx={{ pl: 2 }}>
             <ListItem sx={{ py: 0.5 }}>
               <ListItemIcon sx={{ minWidth: 20 }}>
                 <BulletIcon sx={{ fontSize: 8, color: '#6b7280' }} />
               </ListItemIcon>
               <ListItemText 
                 primary={
                   <Box>
                     <Box component="span" sx={{ fontWeight: 'bold' }}>Access</Box>, <Box component="span" sx={{ fontWeight: 'bold' }}>correct</Box>, or <Box component="span" sx={{ fontWeight: 'bold' }}>delete</Box> your <Box component="span" sx={{ fontWeight: 'bold' }}>personal information</Box>
                   </Box>
                 }
                 sx={{ '& .MuiListItemText-primary': { color: '#374151', fontSize: '14px' } }}
               />
             </ListItem>
             <ListItem sx={{ py: 0.5 }}>
               <ListItemIcon sx={{ minWidth: 20 }}>
                 <BulletIcon sx={{ fontSize: 8, color: '#6b7280' }} />
               </ListItemIcon>
               <ListItemText 
                 primary={
                   <Box>
                     <Box component="span" sx={{ fontWeight: 'bold' }}>Object to</Box> or <Box component="span" sx={{ fontWeight: 'bold' }}>restrict</Box> certain processing activities
                   </Box>
                 }
                 sx={{ '& .MuiListItemText-primary': { color: '#374151', fontSize: '14px' } }}
               />
             </ListItem>
             <ListItem sx={{ py: 0.5 }}>
               <ListItemIcon sx={{ minWidth: 20 }}>
                 <BulletIcon sx={{ fontSize: 8, color: '#6b7280' }} />
               </ListItemIcon>
               <ListItemText 
                 primary={
                   <Box>
                     Request <Box component="span" sx={{ fontWeight: 'bold' }}>data portability</Box>
                   </Box>
                 }
                 sx={{ '& .MuiListItemText-primary': { color: '#374151', fontSize: '14px' } }}
               />
             </ListItem>
           </List>
           <Typography variant="body1" sx={{ color: '#374151', lineHeight: 1.6, mt: 2 }}>
             You may submit requests regarding your account or <Box component="span" sx={{ fontWeight: 'bold' }}>Portal</Box> data directly to <Box component="span" sx={{ fontWeight: 'bold' }}>support@agentzena.ai</Box>.
           </Typography>
         </Box>

         {/* 9. International Data Transfers */}
         <Box sx={{ mb: 4 }}>
           <Typography variant="h5" sx={{ fontWeight: 600, color: '#111827', mb: 2 }}>
             9. International Data Transfers
           </Typography>
           <Typography variant="body1" sx={{ color: '#374151', lineHeight: 1.6 }}>
             <Box component="span" sx={{ fontWeight: 'bold' }}>AgentZena</Box> operates from the <Box component="span" sx={{ fontWeight: 'bold' }}>United States</Box> and may process data using <Box component="span" sx={{ fontWeight: 'bold' }}>service providers</Box> located in other jurisdictions. Where required, we rely on appropriate safeguards, such as <Box component="span" sx={{ fontWeight: 'bold' }}>Standard Contractual Clauses</Box>, to protect <Box component="span" sx={{ fontWeight: 'bold' }}>personal data</Box> transferred internationally.
           </Typography>
         </Box>

         {/* 10. Use by Children */}
         <Box sx={{ mb: 4 }}>
           <Typography variant="h5" sx={{ fontWeight: 600, color: '#111827', mb: 2 }}>
             10. Use by Children
           </Typography>
           <Typography variant="body1" sx={{ color: '#374151', lineHeight: 1.6 }}>
             <Box component="span" sx={{ fontWeight: 'bold' }}>AgentZena</Box> is not intended for individuals under the age of 16, and we do not knowingly collect <Box component="span" sx={{ fontWeight: 'bold' }}>personal data</Box> from children.
           </Typography>
         </Box>

         {/* 11. Changes to This Policy */}
         <Box sx={{ mb: 4 }}>
           <Typography variant="h5" sx={{ fontWeight: 600, color: '#111827', mb: 2 }}>
             11. Changes to This Policy
           </Typography>
           <Typography variant="body1" sx={{ color: '#374151', lineHeight: 1.6 }}>
             We may update this <Box component="span" sx={{ fontWeight: 'bold' }}>Privacy Policy</Box> from time to time. Updates will be posted with a revised "<Box component="span" sx={{ fontWeight: 'bold' }}>Effective Date</Box>."
           </Typography>
         </Box>

         {/* Contact Us */}
         <Box sx={{ mb: 4, mt: 4, pt: 3, borderTop: '1px solid #e5e7eb' }}>
           <Typography variant="h5" sx={{ fontWeight: 600, color: '#111827', mb: 2 }}>
             Contact Us
           </Typography>
           <Typography variant="body1" sx={{ color: '#374151', lineHeight: 1.6 }}>
             If you have any questions about this <Box component="span" sx={{ fontWeight: 'bold' }}>Privacy Policy</Box> or our data practices, please contact us at <Box component="span" sx={{ fontWeight: 'bold' }}>support@agentzena.ai</Box>.
           </Typography>
         </Box>

      </Paper>
    </Container>
  );
}

export default PrivacyPolicy;
